import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Checkbox, CircularProgress, ListItemText, MenuItem, TextField, Typography } from "@material-ui/core";
import api from "../../services/api";
export function parseTelegramTopics(value) {
  try { const data = typeof value === "string" ? JSON.parse(value) : value; return data && typeof data === "object" && !Array.isArray(data) ? data : {}; } catch { return {}; }
}
const GENERAL_TOPIC = 1;
export const MAX_TOPICS_PER_GROUP = 20;
/** Un tema se guarda como número; varios, como lista. */
export const topicIdsOf = value => (Array.isArray(value) ? value : value ? [value] : []).map(Number).filter(Boolean);
export const storedTopics = ids => (ids.length === 0 ? null : ids.length === 1 ? ids[0] : ids);
export default function TelegramCampaignTopicSelect({ group, value, onChange, disabled }) {
  const generation = useRef(0);
  const [search, setSearch] = useState("");
  const [topics, setTopics] = useState([]);
  const [next, setNext] = useState(null);
  const [canPostClosed, setCanPostClosed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const path = `/groups/${group.whatsappId}/${encodeURIComponent(group.groupId)}/telegram-topics`;
  useEffect(() => {
    generation.current += 1;
    let active = true;
    setLoading(true); setError(""); setNext(null);
    const timer = setTimeout(() => {
      api.get(path, { params: { q: search } }).then(({ data }) => {
        if (active) { setTopics(data.topics || []); setNext(data.next); setCanPostClosed(!!data.canPostClosed); }
      }).catch(() => { if (active) setError("No se pudieron cargar los temas. Reintenta antes de elegir el destino."); })
        .finally(() => { if (active) setLoading(false); });
    }, search ? 300 : 0);
    return () => { active = false; clearTimeout(timer); };
  }, [path, search, retry]);
  const loadMore = async () => {
    const currentGeneration = generation.current;
    setLoading(true); setError("");
    try {
      const { data } = await api.get(path, { params: { q: search, ...next } });
      if (currentGeneration !== generation.current) return;
      setTopics(previous => [...new Map([...previous, ...(data.topics || [])].map(topic => [topic.id, topic])).values()]);
      setNext(data.next);
    } catch { if (currentGeneration === generation.current) setError("No se pudieron cargar más temas."); }
    finally { if (currentGeneration === generation.current) setLoading(false); }
  };
  const selected = topicIdsOf(value);
  const listed = topics.filter(topic => topic.id !== GENERAL_TOPIC);
  const missing = selected.filter(id => id !== GENERAL_TOPIC && !listed.some(topic => topic.id === id));
  const titleOf = id => id === GENERAL_TOPIC ? "General" : listed.find(topic => topic.id === id)?.title || `Tema #${id}`;
  const full = selected.length >= MAX_TOPICS_PER_GROUP;
  return (
    <Box px={2} pb={2} pt={1} style={{ minWidth: 0 }}>
      <Typography variant="body2" gutterBottom>Enviar a uno o varios temas de {group.name}</Typography>
      <TextField id={`topic-search-${group.whatsappId}-${group.groupId}`} fullWidth size="small" variant="outlined" label="Buscar tema (ej. Publicidad)"
        value={search} onChange={event => setSearch(event.target.value)} disabled={disabled} />
      <TextField id={`topic-destination-${group.whatsappId}-${group.groupId}`} select InputLabelProps={{ shrink: true }} fullWidth size="small" variant="outlined" margin="dense" label="Temas de destino"
        SelectProps={{ multiple: true, displayEmpty: true, renderValue: ids => ids.length ? ids.map(titleOf).join(", ") : "General (predeterminado)" }}
        value={selected} onChange={event => onChange(storedTopics(topicIdsOf(event.target.value).slice(0, MAX_TOPICS_PER_GROUP)))} disabled={disabled || loading}
        helperText={`Se envía una copia en cada tema marcado (hasta ${MAX_TOPICS_PER_GROUP}). Sin temas, se envía a General. ${canPostClosed
          ? "Tu cuenta es admin con permiso para gestionar temas: también puede publicar en los cerrados."
          : "Los temas cerrados solo se pueden elegir si tu cuenta es admin con permiso para gestionar temas."}`}>
        <MenuItem value={GENERAL_TOPIC} disabled={full && !selected.includes(GENERAL_TOPIC)}>
          <Checkbox size="small" checked={selected.includes(GENERAL_TOPIC)} /><ListItemText primary="General" />
        </MenuItem>
        {missing.map(id => <MenuItem key={`saved-${id}`} value={id}>
          <Checkbox size="small" checked /><ListItemText primary={`Tema #${id} (selección guardada)`} />
        </MenuItem>)}
        {listed.map(topic => <MenuItem key={topic.id} value={topic.id} disabled={!selected.includes(topic.id) && ((topic.closed && !canPostClosed) || topic.hidden || full)}>
          <Checkbox size="small" checked={selected.includes(topic.id)} />
          <ListItemText primary={`${topic.title}${topic.closed ? " · Cerrado" : topic.hidden ? " · Oculto" : ""}`} />
        </MenuItem>)}
      </TextField>
      {loading && <CircularProgress size={18} aria-label="Cargando temas" />}
      {error && <Box><Typography variant="caption" color="error">{error}</Typography><Button size="small" disabled={disabled || loading} onClick={() => setRetry(n => n + 1)}>Reintentar</Button></Box>}
      {next && !error && <Button size="small" disabled={disabled || loading} onClick={loadMore}>Cargar más temas</Button>}
    </Box>
  );
}
